import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, TrendingUp, Sun, Moon, Star, Sparkles } from 'lucide-react';

interface PersonalCyclesProps {
  numerologyProfile: any; // Complete numerology profile
  compact?: boolean;
  embedded?: boolean;
}

/**
 * Personal Cycles Component
 * 
 * Displays user's current numerological cycles:
 * - Personal Year (main theme for the year)
 * - Personal Month (current month's energy)
 * - Personal Day (today's vibration)
 */
const PersonalCycles: React.FC<PersonalCyclesProps> = ({ numerologyProfile, compact = false, embedded = false }) => {
  if (!numerologyProfile) {
    return null;
  }

  const { currentYear, currentMonth, currentDay } = numerologyProfile;

  const cycleData = [
    {
      name: 'Personal Year',
      number: currentYear.personalYearNumber,
      title: currentYear.meaning.title,
      theme: currentYear.theme,
      guidance: currentYear.guidance,
      color: currentYear.meaning.color,
      icon: Sun,
      description: 'Your main theme and lesson for this entire year',
      frequency: currentYear.meaning.frequency
    },
    {
      name: 'Personal Month',
      number: currentMonth,
      title: numerologyProfile.lifePath.meaning.title, // Simplified for now
      color: '#10b981',
      icon: Moon,
      description: 'The energy and focus for this month',
      frequency: 528
    },
    {
      name: 'Personal Day',
      number: currentDay,
      title: 'Today\'s Vibration',
      color: '#f59e0b',
      icon: Star,
      description: 'The unique energy of today',
      frequency: 639
    }
  ];

  if (compact) {
    return (
      <div className="personal-cycles-compact">
        <div className="cycles-grid">
          {cycleData.map((cycle, index) => {
            const Icon = cycle.icon;
            return (
              <motion.div
                key={cycle.name}
                className="cycle-badge"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Icon size={16} style={{ color: cycle.color }} />
                <span className="cycle-number">{cycle.number}</span>
                <span className="cycle-label">{cycle.name.replace('Personal ', '')}</span>
              </motion.div>
            );
          })}
        </div>

        <style>{`
          .personal-cycles-compact {
            padding: 1rem;
            --pc-font-sans: var(--fr00t-font-primary, 'Poppins', sans-serif);
            --pc-font-mono: var(--fr00t-font-mono, 'Courier New', monospace);
            --pc-label-size: 0.8rem;
            --pc-body-size: 0.9rem;
            --pc-tracking: 0.05em;
            font-family: var(--pc-font-sans);
          }

          .cycles-grid {
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
          }

          .cycle-badge {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            background: rgba(139, 92, 246, 0.1);
            border: 1px solid rgba(139, 92, 246, 0.3);
            border-radius: 8px;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }

          .cycle-number {
            font-size: 1.25rem;
            font-weight: 700;
            color: #ffffff;
          }

          .cycle-label {
            font-size: var(--pc-label-size);
            font-family: var(--pc-font-sans);
            color: rgba(255, 255, 255, 0.7);
            text-transform: uppercase;
            letter-spacing: var(--pc-tracking);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={`personal-cycles-full ${embedded ? 'embedded' : 'standalone'}`}>
      <div className="cycles-header">
        <Calendar className="header-icon" />
        <h3>Your Current Cycles</h3>
        <p className="subtitle">Numerological rhythms guiding your journey</p>
      </div>

      <div className="cycles-container">
        {cycleData.map((cycle, index) => {
          const Icon = cycle.icon;
          
          return (
            <motion.div
              key={cycle.name}
              className="cycle-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
              whileHover={{ scale: 1.02, y: -5 }}
            >
              {/* Cycle Header */}
              <div className="cycle-header">
                <div className="cycle-icon-wrapper" style={{ backgroundColor: `${cycle.color}20` }}>
                  <Icon size={24} style={{ color: cycle.color }} />
                </div>
                <div className="cycle-info">
                  <span className="cycle-name">{cycle.name}</span>
                  <span className="cycle-number-large" style={{ color: cycle.color }}>
                    {cycle.number}
                  </span>
                </div>
              </div>

              {/* Cycle Content */}
              <div className="cycle-content">
                <h4 className="cycle-title">{cycle.title}</h4>
                {cycle.theme && (
                  <div className="cycle-theme">
                    <TrendingUp size={16} />
                    <span>{cycle.theme}</span>
                  </div>
                )}
                <p className="cycle-description">{cycle.description}</p>
                {cycle.guidance && (
                  <div className="cycle-guidance">
                    <Sparkles size={14} />
                    <span>{cycle.guidance}</span>
                  </div>
                )}
              </div>

              {/* Frequency Badge */}
              {cycle.frequency && (
                <div className="frequency-badge">
                  <span className="frequency-label">Frequency:</span>
                  <span className="frequency-value">{cycle.frequency} Hz</span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      <style>{`
        .personal-cycles-full {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
          padding: clamp(1rem, 1.8vw, 1.5rem);
          --pc-font-sans: var(--fr00t-font-primary, 'Poppins', sans-serif);
          --pc-font-mono: var(--fr00t-font-mono, 'Courier New', monospace);
          --pc-label-size: 0.8rem;
          --pc-body-size: 0.9rem;
          --pc-tracking: 0.05em;
          font-family: var(--pc-font-sans);
        }

        .personal-cycles-full.standalone {
          max-width: 1200px;
          margin: 0 auto;
        }

        .personal-cycles-full.embedded {
          padding: 0;
          margin: 0;
        }

        .cycles-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .personal-cycles-full.embedded .cycles-header {
          text-align: left;
          margin-bottom: 1.25rem;
        }

        .header-icon {
          margin: 0 auto 1rem;
          color: #8b5cf6;
        }

        .personal-cycles-full.embedded .header-icon {
          margin: 0 0 0.75rem 0;
        }

        .cycles-header h3 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .subtitle {
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .cycles-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
          gap: 1.5rem;
          min-width: 0;
        }

        .cycle-card {
          background: rgba(139, 92, 246, 0.1);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 16px;
          padding: 1.5rem;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          min-width: 0;
        }

        .cycle-card:hover {
          border-color: rgba(139, 92, 246, 0.5);
          box-shadow: 0 8px 32px rgba(139, 92, 246, 0.2);
        }

        .cycle-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .cycle-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .cycle-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .cycle-name {
          font-size: var(--pc-label-size);
          font-family: var(--pc-font-sans);
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: var(--pc-tracking);
          font-weight: 600;
        }

        .cycle-number-large {
          font-size: 2rem;
          font-weight: 700;
          line-height: 1;
        }

        .cycle-content {
          margin-bottom: 1rem;
        }

        .cycle-title {
          font-size: 1.15rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.75rem;
        }

        .cycle-theme {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          padding: 0.5rem;
          background: rgba(16, 185, 129, 0.1);
          border-radius: 8px;
          color: #10b981;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .cycle-description {
          font-size: var(--pc-body-size);
          font-family: var(--pc-font-sans);
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.5;
          margin-bottom: 0.75rem;
        }

        .cycle-guidance {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          padding: 0.75rem;
          background: rgba(245, 158, 11, 0.1);
          border-radius: 8px;
          border-left: 3px solid #f59e0b;
          font-size: var(--pc-body-size);
          font-family: var(--pc-font-sans);
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.4;
        }

        .frequency-badge {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 0.75rem;
          background: rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          margin-top: 1rem;
        }

        .frequency-label {
          font-size: var(--pc-label-size);
          font-family: var(--pc-font-sans);
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: var(--pc-tracking);
        }

        .frequency-value {
          font-size: var(--pc-body-size);
          font-weight: 700;
          color: #8b5cf6;
          font-family: var(--pc-font-mono);
        }
      `}</style>
    </div>
  );
};

export default PersonalCycles;
